import { useContext, useEffect, useRef, useState } from 'react';
import {
  AgxButton,
  AgxNotificationBox,
  AgxSpacer,
  AgxTypography,
  AgxTab,
  useToast,
} from '@urbanx/agx.ui-components2';
import { FormConfigType, MarketingPackageStage, AustralianState } from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { AuthContext } from '@urbanx/agx.auth.client';
import { beginCampaignForm } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { useFeatureToggles } from 'hooks/useFeatureToggles';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import ListingDetails, { ListingDetailsHandle } from './Common/ListingDetails';
import ContractDetails from './Common/ContractDetails';
import MarketingPackageSigningPanel from './MarketingPackage/MarketingPackageSigningPanel';
import ResumeMarketingPackage from './MarketingPackage/ResumeMarketingPackage';
import MarketingPackageCompletePanel from './MarketingPackage/MarketingPackageCompletePanel';
import { Tabs, ListingTabs } from 'types/Tabs';
import './ListingInfoPanel.scss';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  FormType,
} from '@urbanx/agx.campaigns.client';
import LaunchDetails from './Common/LaunchDetails';
import {
  type MarketingPackageProjection,
  useGetMarketingPackages,
  useInvalidateUseGetMarketingPackages,
} from '@urbanx/agx.marketing.client';
import ContractFormButtons from './Common/ContractFormButtons';

interface ListingInfoPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
  selectedMarketingPackage?: MarketingPackageProjection;
  setSelectedMarketingPackage: (marketingPackage: MarketingPackageProjection | undefined) => void;
}

const ListingInfoPanel = ({
  campaign,
  currentTab,
  mobile = false,
  selectedMarketingPackage,
  setSelectedMarketingPackage,
}: ListingInfoPanelProps) => {
  const listingDetailsRef = useRef<ListingDetailsHandle>(null);
  const [selectedTab, setSelectedTab] = useState(ListingTabs.Contracts);
  const [disableButtons, setDisableButtons] = useState(false);
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm } = useFormLoader();
  const { pushToast } = useToast();
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const { addMarketingToLiveListing, enableLaunchPad } = useFeatureToggles(campaign?.state);
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;

  const isArchived = !!campaign.completionState?.completionType;

  const { address, campaignId, blankContractIsLive, lastUpdatedUtc } = campaign;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const { data: marketingPackages } = useGetMarketingPackages(
    campaign.campaignId ?? '',
    addMarketingToLiveListing
  );

  const invalidateMarketingPackages = useInvalidateUseGetMarketingPackages();

  useEffect(() => {
    invalidateMarketingPackages(campaign.campaignId ?? '');
  }, [lastUpdatedUtc]);

  useEffect(() => {
    if (!selectedMarketingPackage) return;

    setSelectedMarketingPackage(
      marketingPackages?.find((mp) => mp.id === selectedMarketingPackage.id)
    );
  }, [marketingPackages]);

  const tabOptions = [
    {
      id: ListingTabs.Contracts,
      label: 'Contracts',
      selected: selectedTab === ListingTabs.Contracts,
    },
    {
      id: ListingTabs.ListingDetails,
      label: 'Listing Details',
      selected: selectedTab === ListingTabs.ListingDetails,
    },
    ...(enableLaunchPad
      ? [
          {
            id: ListingTabs.Launch,
            label: 'Launch',
            selected: selectedTab === ListingTabs.Launch,
          },
        ]
      : []),
  ];

  const findAvailableForm = (formType: FormType) =>
    availableForms.find(
      (form) => form.type === formType && (form.state as string | undefined) === campaign.state
    );

  const beginForm = async (formType: FormType, errorMessage: string) => {
    if (!getAuthToken) return;

    const form = findAvailableForm(formType);

    if (!form) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newFormId = await beginCampaignForm(
        authToken,
        campaignId ?? '',
        form.type as FormType,
        form.state as AustralianState
      );

      if (!newFormId) {
        pushToast({
          type: 'error',
          message: errorMessage,
        });
        setDisableButtons(false);
        return;
      }

      await loadForm(campaignId ?? '', newFormId, FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
    }

    setDisableButtons(false);
  };

  const beginContract = () => beginForm(FormType.ContractRequest, 'Failed to start Sales Advice');

  const beginContractExchange = () =>
    beginForm(FormType.ContractExchange, 'Failed to start Contract exchange');

  const beginSubmission = () =>
    beginForm(FormType.ContractSubmission, 'Failed to start Contract submission');

  if (selectedMarketingPackage) {
    switch (selectedMarketingPackage.stage?.value) {
      case MarketingPackageStage.Draft:
      case MarketingPackageStage.SubmissionIncomplete:
        return (
          <ResumeMarketingPackage
            onBack={() => setSelectedMarketingPackage(undefined)}
            campaign={campaign}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      case MarketingPackageStage.VendorSigning:
        return (
          <MarketingPackageSigningPanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
            campaign={campaign}
          />
        );
      case MarketingPackageStage.Complete:
        return (
          <MarketingPackageCompletePanel
            onBack={() => setSelectedMarketingPackage(undefined)}
            marketingPackage={selectedMarketingPackage}
            addressLineOne={addressLineOne}
            addressLineTwo={addressLineTwo}
          />
        );
      default:
        break;
    }
  }

  return (
    <>
      <AgxSpacer
        className="listingInfoPanel"
        contentAlignment="center"
        gap="md"
        padding={{ bottom: 'sm' }}
      >
        <AgxSpacer fill contentAlignment="center">
          <AgxTypography
            id="agx-listing-info-panel-address"
            type="heading"
            size="2x-large"
            style={{
              textAlign: 'center',
            }}
          >
            {addressLineOne},
            <br />
            {addressLineTwo}
          </AgxTypography>
        </AgxSpacer>
        <AgxSpacer fill contentAlignment="center">
          <AgxTab
            options={tabOptions}
            onChange={(tab) => setSelectedTab(tab.id as ListingTabs)}
            fill
          />
        </AgxSpacer>

        {selectedTab === ListingTabs.Contracts && (
          <ContractDetails
            campaign={campaign}
            contractDetails={campaign.contractDetails ?? []}
            completionState={campaign.completionState ?? {}}
            propertyImageUrls={{
              thumbnail: campaign.propertyImageUrls?.thumbnail,
            }}
            mobile={mobile}
          />
        )}

        {selectedTab === ListingTabs.ListingDetails && (
          <ListingDetails
            ref={listingDetailsRef}
            campaign={campaign}
            mobile={mobile}
            marketingPackages={marketingPackages ?? []}
            setSelectedMarketingPackage={setSelectedMarketingPackage}
          />
        )}
        {selectedTab === ListingTabs.Launch && <LaunchDetails campaign={campaign} />}

        {(!isNsw && !campaign.blankContractIsLive && (campaign.contractDetails?.length ?? 0) > 0) ||
        (isNsw && !campaign.areVendorsValid) ? (
          <div className="offlineContractAlert">
            <AgxNotificationBox
              type="warning"
              heading="New Contract generation is disabled."
              body={mobile ? 'Contact Support' : 'Contact your administrator'}
            />
          </div>
        ) : null}

        {selectedTab === ListingTabs.Contracts && (
          <ContractFormButtons
            beginSubmission={beginSubmission}
            beginContract={beginContract}
            beginContractExchange={beginContractExchange}
            campaignState={campaign.state ?? CampaignDetailModelState.QLD}
            areVendorsValid={campaign.areVendorsValid ?? false}
            disableButtons={disableButtons}
            isArchived={isArchived}
            blankContractIsLive={blankContractIsLive ?? false}
          />
        )}
        {selectedTab === ListingTabs.Launch && (
          <AgxButton
            id="agx-infoPanel-reviewAndLaunchButton"
            variant="hollow"
            text="Review & Launch"
            size="large"
            stretch
            onClick={() => {}}
          />
        )}
      </AgxSpacer>
    </>
  );
};

export default ListingInfoPanel;
